import {
  AscendingOrderArray,
  ENTITY_CLASS,
  ERROR_CODE,
  RESIDENT_STATE,
  type ResidentState,
  SensorKey,
  Status,
  StatusQuery,
  UniversalError,
  UserKey,
  isSensorKey,
} from "@packages/core";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Temporal } from "temporal-polyfill";

import { findStatuses } from "./functions";
import { QueryKey } from "./query";
import { NETWORK_SEMAPHORE } from "./semaphore";
import { TANSTACK_QUERY_CLIENT } from "./tanstack";

export function fromResidentStateToColor(
  residentState: ResidentState,
  options?: Partial<{
    /** **簡単モード** `true`の時、いくつかの入居者状態にそれぞれ同じ色を割り当てる。 */
    easy: boolean;
  }>,
): string {
  switch (residentState) {
    case RESIDENT_STATE.CAME_BACK:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.UNKNOWN) : "#FFB6C1";
    case RESIDENT_STATE.GOT_UP:
      return "#B3DAEE";
    case RESIDENT_STATE.PROVIDER_ERROR:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.UNKNOWN) : "#C71585";
    case RESIDENT_STATE.SENSOR_ERROR:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.UNKNOWN) : "#DC143C";
    case RESIDENT_STATE.SLEEPING:
      return "#0284C7";
    case RESIDENT_STATE.SLEEPING_HEAVILY:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.SLEEPING) : "#0369A1";
    case RESIDENT_STATE.SLEEPING_LIGHTLY:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.SLEEPING) : "#479BC3";
    case RESIDENT_STATE.UNKNOWN:
      return "#C0C0C0";
    case RESIDENT_STATE.WAKING:
      return "#4EA9D8";
    case RESIDENT_STATE.WANDERING:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.GOT_UP) : "#F0E68C";
    case RESIDENT_STATE.WENT_OUT:
      return options?.easy ? fromResidentStateToColor(RESIDENT_STATE.GOT_UP) : "#3CB371";
    default:
      throw new UniversalError(
        ERROR_CODE.UNEXPECTED,
        "入居者状態" + (residentState satisfies never) + "に対応する色が定義されていません。",
      );
  }
}

export function renderStatusChart(
  parameters: {
    statuses: AscendingOrderArray<Pick<Status, "state" | "transitionedAt">>;
    from: Temporal.PlainDateTime;
    to: Temporal.PlainDateTime;
    numberOfPixels: number;
  },
  options?: Partial<{
    /** **簡単モード** `true`の時、いくつかの入居者状態にそれぞれ同じ色を割り当てる。 */
    easy?: boolean;
  }>,
) {
  const canvas = document.createElement("canvas");
  canvas.height = 1;
  canvas.width = parameters.numberOfPixels;
  const context = canvas.getContext("2d");

  const millisecondsPerPixel =
    parameters.to.since(parameters.from).total("milliseconds") /
    Math.max(Math.round(parameters.numberOfPixels), 0);

  const now = Temporal.Now.plainDateTimeISO("Asia/Tokyo");

  let k = 0;
  for (let i = 0; i < canvas.width; i++) {
    const dateTime = parameters.from.add({ milliseconds: Math.round(millisecondsPerPixel * i) });

    while (k < parameters.statuses.length) {
      const { transitionedAt: transitionedAt1, state } = parameters.statuses[k];
      const transitionedAt2 = parameters.statuses.at(k + 1)?.transitionedAt;

      if (
        Temporal.PlainDateTime.compare(now, dateTime) <= 0 ||
        Temporal.PlainDateTime.compare(transitionedAt1, dateTime) > 0
      ) {
        break;
      } else if (
        transitionedAt2 &&
        Temporal.PlainDateTime.compare(transitionedAt2, dateTime) <= 0
      ) {
        k++;
        continue;
      } else if (Temporal.PlainDateTime.compare(transitionedAt1, dateTime) <= 0) {
        if (context) {
          context.fillStyle = fromResidentStateToColor(state, options);
          context.fillRect(i, 0, 1, 1);
        }
        break;
      }
    }
  }

  return canvas;
}

async function findLatestStatus(
  sensorKeyOrUserKey: SensorKey | UserKey | false | null | undefined,
) {
  if (!sensorKeyOrUserKey) {
    return undefined;
  }

  const { statuses } = await findStatuses(
    isSensorKey(sensorKeyOrUserKey)
      ? {
          index: "sensorKey-transitionedAt",
          condition: [["sensorKey", "=", sensorKeyOrUserKey]],
          descendingOrder: true,
          limit: 1,
        }
      : {
          index: "userKey-transitionedAt",
          condition: [["userKey", "=", sensorKeyOrUserKey]],
          descendingOrder: true,
          limit: 1,
        },
  );

  return statuses.at(0) ?? null;
}

function LatestStatusQueryKey(sensorKeyOrUserKey: SensorKey | UserKey | false | null | undefined) {
  return QueryKey(ENTITY_CLASS.STATUS + "::Latest", sensorKeyOrUserKey);
}

export function updateLatestStatusQueryCache(
  sensorKeyOrUserKey: SensorKey | UserKey | false | null | undefined,
  newStatus?: ReturnType<typeof useLatestStatusQuery>["data"] | false,
) {
  if (sensorKeyOrUserKey && newStatus !== false && newStatus !== undefined) {
    TANSTACK_QUERY_CLIENT.setQueryData(
      LatestStatusQueryKey(sensorKeyOrUserKey),
      function (
        oldStatus: ReturnType<typeof useLatestStatusQuery>["data"],
      ): ReturnType<typeof useLatestStatusQuery>["data"] {
        return !oldStatus ||
          !newStatus ||
          (Temporal.PlainDateTime.compare(oldStatus.transitionedAt, newStatus.transitionedAt) ||
            Temporal.PlainDateTime.compare(oldStatus.createdAt, newStatus.createdAt) ||
            Temporal.PlainDateTime.compare(oldStatus.updatedAt, newStatus.updatedAt)) < 0
          ? newStatus
          : oldStatus;
      },
    );
  }
}

export function useLatestStatusQuery(
  sensorKeyOrUserKey: SensorKey | UserKey | false | null | undefined,
) {
  return useQuery({
    queryKey: LatestStatusQueryKey(sensorKeyOrUserKey),
    async queryFn() {
      return findLatestStatus(sensorKeyOrUserKey);
    },

    enabled: Boolean(sensorKeyOrUserKey),
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}

// =============================================================
// 一覧
// =============================================================

function StatusInfiniteQueryKey(...parameters: Parameters<typeof useStatusInfiniteQuery>) {
  return QueryKey(ENTITY_CLASS.STATUS + "List", ...parameters);
}

export function useStatusInfiniteQuery(
  query: StatusQuery,
  options?: Partial<{
    /** 処理を実行する場合に`true`を設定するフラグ */
    enabled: boolean;
  }>,
) {
  return useInfiniteQuery({
    queryKey: StatusInfiniteQueryKey(query, options),
    async queryFn(context) {
      const release = await NETWORK_SEMAPHORE.acquire();
      try {
        const { statuses, errors, nextPageToken } = await findStatuses({
          ...query,
          ...context.pageParam,
        });

        for (const status of statuses) {
          if (status.sensorKey) {
            updateLatestStatusQueryCache(status.sensorKey, status);
          }
          if (status.userKey) {
            updateLatestStatusQueryCache(status.userKey, status);
          }
        }

        return { statuses, errors, nextPageToken };
      } finally {
        release();
      }
    },

    getNextPageParam(lastPage) {
      return lastPage.nextPageToken ? { nextPageToken: lastPage.nextPageToken } : undefined;
    },
    initialPageParam: {} as { nextPageToken?: Record<string, any> },

    enabled: options?.enabled,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
    refetchOnWindowFocus: true,
    staleTime: /** 4時間 */ 14_400_000,
  });
}
